import { useEffect, useMemo, useState } from "react";
import { Col } from "reactstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faPenToSquare,
  faTrash,
  faListCheck,
} from "@fortawesome/free-solid-svg-icons";
import { APISD } from "../../../../../../config/apisd";
import ModalFormTp from "../modal/ModalTP";

export default function TpPage({
  selectedMatpel,
  selectedCp,
  setSelectedCp,
  getToken,
  getUserId,
  nameCollator,
  ToastSuccess,
  ToastError,
}) {
  const [tpDataByCp, setTpDataByCp] = useState({});
  const [formTp, setFormTp] = useState({
    tp: "",
    keterangan: "",
    pengujian: "",
    deskripsi_rapor_semester_1: "",
    deskripsi_rapor_semester_2: "",
  });

  const [editTpId, setEditTpId] = useState(null);
  const [searchTp, setSearchTp] = useState("");
  const [showModalTp, setShowModalTp] = useState(false);
  const [isFetchingTp, setIsFetchingTp] = useState(false);
  const [isSubmittingTp, setIsSubmittingTp] = useState(false);
  const [deletingTpId, setDeletingTpId] = useState(null);

  const getTpArrayFromResponse = (responseData) => {
    if (Array.isArray(responseData?.data?.data)) return responseData.data.data;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.result)) return responseData.result;
    if (Array.isArray(responseData)) return responseData;
    return [];
  };

  const groupTpByCp = (rows = []) => {
    return rows.reduce((acc, item) => {
      const cpId =
        item?.ysb_matpel_cp_id ||
        item?.matpel_cp_id ||
        item?.cp_id ||
        "tanpa_cp";

      if (!acc[cpId]) acc[cpId] = [];

      const description1 = item?.description_1 || "";
      const description2 = item?.description_2 || "";

      let pengujian = "";
      if (description1) pengujian = "Semester 1";
      if (description2) pengujian = "Semester 2";

      acc[cpId].push({
        id: item?.id,
        ysb_matpel_cp_id: item?.ysb_matpel_cp_id || cpId,
        tp: item?.name_tp || item?.tp || "",
        keterangan: item?.name_information || item?.keterangan || "",
        pengujian,
        deskripsi_rapor_semester_1: description1,
        deskripsi_rapor_semester_2: description2,
        raw: item,
      });

      return acc;
    }, {});
  };

  const getTpData = async () => {
    setIsFetchingTp(true);

    try {
      const token = getToken();

      const response = await APISD.get("/api/matpels-tp", {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        params: {
          page: 1,
          limit: 1000,
          ascending: 0,
          search: "",
          branch: "",
          level: localStorage.getItem("level") || "developer",
        },
      });

      const rows = getTpArrayFromResponse(response?.data);
      setTpDataByCp(groupTpByCp(rows));
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title: error?.response?.data?.message || "Gagal mengambil data TP",
      });
    } finally {
      setIsFetchingTp(false);
    }
  };

  useEffect(() => {
    if (selectedCp?.id) {
      getTpData();
      setSearchTp("");
      resetFormTp();
    }
  }, [selectedCp?.id]);

  const currentTpData = useMemo(() => {
    if (!selectedCp?.id) return [];
    return tpDataByCp[selectedCp.id] || [];
  }, [tpDataByCp, selectedCp]);

  const filteredTpData = useMemo(() => {
    const q = (searchTp || "").trim().toLowerCase();

    const base = !q
      ? currentTpData || []
      : (currentTpData || []).filter((item) => {
          const tp = (item?.tp || "").toString().toLowerCase();
          const keterangan = (item?.keterangan || "").toString().toLowerCase();
          const pengujian = (item?.pengujian || "").toString().toLowerCase();
          const deskripsi1 = (item?.deskripsi_rapor_semester_1 || "")
            .toString()
            .toLowerCase();
          const deskripsi2 = (item?.deskripsi_rapor_semester_2 || "")
            .toString()
            .toLowerCase();

          return (
            tp.includes(q) ||
            keterangan.includes(q) ||
            pengujian.includes(q) ||
            deskripsi1.includes(q) ||
            deskripsi2.includes(q)
          );
        });

    return [...base].sort((a, b) =>
      nameCollator.compare(
        (a?.tp || "").toString().trim(),
        (b?.tp || "").toString().trim()
      )
    );
  }, [currentTpData, searchTp, nameCollator]);

  const resetFormTp = () => {
    setFormTp({
      tp: "",
      keterangan: "",
      pengujian: "",
      deskripsi_rapor_semester_1: "",
      deskripsi_rapor_semester_2: "",
    });
    setEditTpId(null);
  };

  const handleOpenAddTp = () => {
    resetFormTp();
    setShowModalTp(true);
  };

  const handleChangeFormTp = (e) => {
    const { name, value } = e.target;

    setFormTp((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitTp = async (e) => {
    e.preventDefault();

    if (!selectedCp?.id) {
      ToastError.fire({
        icon: "error",
        title: "CP belum dipilih",
      });
      return;
    }

    if (!formTp.tp.trim()) {
      ToastError.fire({
        icon: "error",
        title: "TP wajib diisi",
      });
      return;
    }

    if (!formTp.keterangan.trim()) {
      ToastError.fire({
        icon: "error",
        title: "Keterangan wajib diisi",
      });
      return;
    }

    if (!formTp.pengujian.trim()) {
      ToastError.fire({
        icon: "error",
        title: "Pengujian wajib dipilih",
      });
      return;
    }

    const token = getToken();
    const userId = getUserId();

    const payload = {
      ysb_matpel_cp_id: selectedCp?.id || "",
      name_tp: formTp.tp.trim(),
      name_information: formTp.keterangan.trim(),
      description_1:
        formTp.pengujian === "Semester 1"
          ? formTp.deskripsi_rapor_semester_1?.trim() || ""
          : "",
      description_2:
        formTp.pengujian === "Semester 2"
          ? formTp.deskripsi_rapor_semester_2?.trim() || ""
          : "",
      user_id: userId,
    };

    setIsSubmittingTp(true);

    try {
      let response;

      if (editTpId) {
        response = await APISD.put(`/api/matpels-tp/${editTpId}`, payload, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });
      } else {
        response = await APISD.post("/api/matpels-tp/store", payload, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });
      }

      ToastSuccess.fire({
        icon: "success",
        title:
          response?.data?.message ||
          (editTpId ? "Data TP berhasil diupdate" : "Data TP berhasil disimpan"),
      });

      resetFormTp();
      setShowModalTp(false);
      await getTpData();
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          (editTpId ? "Gagal update data TP" : "Gagal menyimpan data TP"),
      });
    } finally {
      setIsSubmittingTp(false);
    }
  };

  const handleEditTp = (item) => {
    setEditTpId(item.id);

    setFormTp({
      tp: item.tp || "",
      keterangan: item.keterangan || "",
      pengujian: item.pengujian || "",
      deskripsi_rapor_semester_1: item.deskripsi_rapor_semester_1 || "",
      deskripsi_rapor_semester_2: item.deskripsi_rapor_semester_2 || "",
    });

    setShowModalTp(true);
  };

  const handleDeleteTp = (item) => {
    Swal.fire({
      title: "Apakah Kamu Yakin?",
      text: "Data TP yang dihapus tidak bisa dikembalikan",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, Hapus",
      cancelButtonText: "Batal",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      const token = getToken();
      setDeletingTpId(item.id);

      try {
        const response = await APISD.delete(`/api/matpels-tp/${item.id}`, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });

        ToastSuccess.fire({
          icon: "success",
          title: response?.data?.message || "Data TP berhasil dihapus",
        });

        await getTpData();

        if (editTpId === item.id) {
          resetFormTp();
          setShowModalTp(false);
        }
      } catch (error) {
        ToastError.fire({
          icon: "error",
          title: error?.response?.data?.message || "Gagal menghapus data TP",
        });
      } finally {
        setDeletingTpId(null);
      }
    });
  };

  const handleBackToCp = () => {
    setSelectedCp(null);
    setSearchTp("");
    resetFormTp();
  };

  return (
    <div className="body">
      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faListCheck} />
            </span>

            <div>
              <h4 className="page-title-text mb-0">
                TP - CP {selectedCp?.nama_cp || "-"}
              </h4>
              <small style={{ color: "#6b7280", fontWeight: 500 }}>
                {selectedMatpel?.nama_matpel || "-"}
              </small>
            </div>
          </div>

          <div className="ml-auto">
            <button
              type="button"
              className="btn btn-light"
              onClick={handleBackToCp}
              style={{
                borderRadius: 12,
                padding: "8px 14px",
                fontWeight: 600,
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Kembali
            </button>
          </div>
        </div>

        <nav aria-label="breadcrumb" className="custom-breadcrumb-nav mt-4">
          <ol className="custom-breadcrumb">
            <li className="custom-breadcrumb-item">
              <Link onClick={handleBackToCp}>CP</Link>
            </li>
            <li className="custom-breadcrumb-separator">/</li>
            <li className="custom-breadcrumb-item active" aria-current="page">
              TP
            </li>
          </ol>
        </nav>
      </div>

      <div className="body-content">
        <ModalFormTp
          show={showModalTp}
          onHide={() => {
            if (isSubmittingTp) return;
            setShowModalTp(false);
            resetFormTp();
          }}
          editTpId={editTpId}
          formTp={formTp}
          handleChangeFormTp={handleChangeFormTp}
          handleSubmitTp={handleSubmitTp}
          isSubmittingTp={isSubmittingTp}
        />

        <div className="d-flex mb-4">
          <div className="at-search">
            <input
              className="at-input"
              placeholder="Cari TP, keterangan, pengujian, atau deskripsi"
              value={searchTp}
              onChange={(e) => setSearchTp(e.target.value)}
            />
          </div>

          <div className="ml-auto">
            <button
              type="button"
              onClick={handleOpenAddTp}
              className="btn btn-create mr-2"
              style={{
                borderRadius: 12,
                padding: "8px 14px",
                boxShadow: "0 10px 24px rgba(108,99,255,0.18)",
                fontWeight: 600,
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Buat TP
            </button>
          </div>
        </div>

        <Col xl="12" sm="12">
          <div className="mt-3 body-table">
            {/* <table className="table dt-responsive nowrap w-100"> */}
            <table className="table table-hover table-border-style" id="basic-datatable">
              <thead>
                <tr>
                  <th>TP</th>
                  <th>Keterangan</th>
                  <th>Pengujian</th>
                  <th style={{ width: "220px"}}>Aksi</th>
                </tr>
              </thead>

              <tbody>
                {isFetchingTp ? (
                  <tr>
                    <td colSpan="4" className="text-center" style={{ padding: 20 }}>
                      Mengambil data TP...
                    </td>
                  </tr>
                ) : filteredTpData.length > 0 ? (
                  filteredTpData.map((row, index) => (
                    <tr key={row?.id || index}>
                      <td style={{ lineHeight: "2", fontWeight: 600 }}>
                        {row?.tp || "-"}
                      </td>

                      <td style={{ lineHeight: "2" }}>
                        {row?.keterangan || "-"}
                      </td>

                      <td style={{ lineHeight: "2", textAlign: "center" }}>
                        <span style={{ color: "#00a86b", fontWeight: 700 }}>
                          {row?.pengujian || "-"}
                        </span>
                      </td>

                      <td style={{ textAlign: "center", lineHeight: "2" }}>
                        <div
                          className="d-flex justify-content-center"
                          style={{ gap: 8, flexWrap: "wrap" }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={() => handleEditTp(row)}
                            style={{
                              borderRadius: 8,
                              color: "#fff",
                              fontWeight: 600,
                            }}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteTp(row)}
                            disabled={deletingTpId === row.id}
                            style={{
                              borderRadius: 8,
                              fontWeight: 600,
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center" style={{ padding: 20 }}>
                      Data TP belum tersedia
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Col>
      </div>
    </div>
  );
}